import {groupBy} from '@benchling/js-utils';

import {ProcedureParameterConfirmationMethod} from 'graphql-client/__generated/graphqlGlobalTypes';
import {omit} from 'util/omitKey';

import {BaseProcedureMethodDefinitionVersionGraphQL_ProcedureMethodDefinitionVersionNode as ProcedureMethodDefinitionVersionNode} from '../../../graphql-fragments/procedures/__generated/BaseProcedureMethodDefinitionVersionGraphQL';
import {
  ProcedureMethodDefinitionVersionParameterVersionsGraphQL_ProcedurePlanMethodDefinitionVersionNode_dataParameterSettings_ProcedurePlanDataParameterSettingNode as DataParameterSetting,
  ProcedureMethodDefinitionVersionParameterVersionsGraphQL_ProcedurePlanMethodDefinitionVersionNode_equipmentSettings_ProcedurePlanEquipmentSettingNode as EquipmentSetting,
  ProcedureMethodDefinitionVersionParameterVersionsGraphQL_ProcedurePlanMethodDefinitionVersionNode_materialInputSettings_ProcedurePlanMaterialInputSettingNode as MaterialInputSetting,
} from '../../../graphql-fragments/procedures/__generated/ProcedureMethodDefinitionVersionParameterVersionsGraphQL';
import {ProcedureMethodDefinitionVersionParameterVersionsGraphQL_ProcedurePlanMethodDefinitionVersionNode as BasePlanMethodDefinitionVersionNode} from '../../../graphql-fragments/procedures/__generated/ProcedureMethodDefinitionVersionParameterVersionsGraphQL';
import {
  ProcedureMethodExecutionInstancePlannedValuesGraphQL_ProcedureMethodExecutionInstanceNode_dataParameterPlannedValues_ProcedureDataParameterPlannedValueNode as DataParameterPlannedValue,
  ProcedureMethodExecutionInstancePlannedValuesGraphQL_ProcedureMethodExecutionInstanceNode_equipmentPlannedValues_ProcedureEquipmentPlannedValueNode as EquipmentPlannedValue,
  ProcedureMethodExecutionInstancePlannedValuesGraphQL_ProcedureMethodExecutionInstanceNode_materialInputPlannedValues_ProcedureMaterialInputPlannedValueNode as MaterialInputPlannedValue,
} from '../../../graphql-fragments/procedures/__generated/ProcedureMethodExecutionInstancePlannedValuesGraphQL';

interface MaterialInputSettingWithTemplateValues extends Omit<MaterialInputSetting, '__typename'> {
  templateValues: Array<Omit<MaterialInputPlannedValue, 'parameterVersion'>>;
}

type DataParameterSettingWithValues = Omit<DataParameterSetting, '__typename'> & {
  __typename: 'ProcedureDataParameterSettingNode';
  values: Array<Omit<DataParameterPlannedValue, 'parameterVersion'>>;
};

type MaterialInputParameterSettingWithValues = Omit<MaterialInputSetting, '__typename'> & {
  __typename: 'ProcedureMaterialInputSettingNode';
  values: Array<Omit<MaterialInputPlannedValue, 'parameterVersion'>>;
  // This type must not be included in the output value
  templateValues?: never;
};

type EquipmentSettingWithValues = Omit<EquipmentSetting, '__typename'> & {
  __typename: 'ProcedureEquipmentSettingNode';
  values: Array<Omit<EquipmentPlannedValue, 'parameterVersion'>>;
};

interface PlanMethodDefinitionVersionWithTemplateMaterialInputs
  extends Omit<BasePlanMethodDefinitionVersionNode, 'materialInputSettings'> {
  materialInputSettings: Array<MaterialInputSettingWithTemplateValues>;
}

type ProcedureMethodDefinitionVersionWithPlannedValues = Pick<
  ProcedureMethodDefinitionVersionNode,
  '__typename' | 'materialOutputSettings'
> & {
  dataParameterSettings: Array<DataParameterSettingWithValues>;
  materialInputSettings: Array<MaterialInputParameterSettingWithValues>;
  equipmentSettings: Array<EquipmentSettingWithValues>;
};

/**
 * Given a plan PMDV, fill in the parameter settings with the specified planned values.
 *
 * If the plan PMDV has any pre-existing planned values, they will be overwritten, except for the
 * process intermediate behavior controlled by fallbackToPlanMaterialInputs (see below).
 * This allows us to fetch the planned values off the PMEIs in execution, which will be the source of truth,
 * and populate a full PMDV with these values for downstream callers to use the same shape as in config.
 *
 * @param basePlanMethodVersion The PMDV without values
 * @param dataParameterPlannedValues The planned values for data parameters, from a PMEI
 * @param materialInputPlannedValues The planned values for material inputs, from a PMEI
 * @param equipmentPlannedValues The planned values for equipment, from a PMEI
 * @param parameterConfirmationMethods The confirmation methods for the parameters, from a PMEI
 * @param fallbackToPlanMaterialInputs Whether to fallback to material input values off the material input
 * settings if there are no planned values for a material input. This would be the case for process intermediates,
 * which will not yet have planned values on the PMEI but we may still want to include the template value from
 * config to get the amount. Default is true.
 */
export function fillPlanMethodVersionWithPlannedValuesAndConfirmationMethods(
  basePlanMethodVersion: PlanMethodDefinitionVersionWithTemplateMaterialInputs,
  dataParameterPlannedValues: Array<DataParameterPlannedValue>,
  materialInputPlannedValues: Array<MaterialInputPlannedValue>,
  equipmentPlannedValues: Array<EquipmentPlannedValue>,
  parameterConfirmationMethods: Map<string, ProcedureParameterConfirmationMethod>,
  {fallbackToPlanMaterialInputs = true}: {fallbackToPlanMaterialInputs?: boolean} = {}
): ProcedureMethodDefinitionVersionWithPlannedValues {
  const dataParamValuesByParameterVersionId = groupBy(
    dataParameterPlannedValues,
    (value) => value.parameterVersion.id
  );
  const materialInputValuesByParameterVersionId = groupBy(
    materialInputPlannedValues,
    (value) => value.parameterVersion.id
  );
  const equipmentValuesByParameterVersionId = groupBy(
    equipmentPlannedValues,
    (value) => value.parameterVersion.id
  );
  return {
    ...basePlanMethodVersion,
    __typename: 'ProcedureMethodDefinitionVersionNode',
    dataParameterSettings: basePlanMethodVersion.dataParameterSettings.map((param) => ({
      ...param,
      __typename: 'ProcedureDataParameterSettingNode',
      values: dataParamValuesByParameterVersionId[param.version.id] ?? [],
      version: {
        ...param.version,
        confirmationMethod: parameterConfirmationMethods.get(param.version.procedureDataParameterId)!,
      },
    })),
    // Missing material input planned values can mean that the material is a process intermediate, which
    // will not have varying planned values anyway. If fallbackToPlanMaterialInputs is true (default), we will
    // fill in these gaps with the values from the PMDV in order to get the mat input amounts.
    materialInputSettings: basePlanMethodVersion.materialInputSettings.map((param) => ({
      ...omit(param, 'templateValues'),
      __typename: 'ProcedureMaterialInputSettingNode',
      values:
        materialInputValuesByParameterVersionId[param.version.id] ??
        (fallbackToPlanMaterialInputs ? (param.templateValues ?? []) : []),
      version: {
        ...param.version,
        confirmationMethod: parameterConfirmationMethods.get(param.version.procedureMaterialInputId)!,
      },
    })),
    equipmentSettings: basePlanMethodVersion.equipmentSettings.map((param) => ({
      ...param,
      __typename: 'ProcedureEquipmentSettingNode',
      values: equipmentValuesByParameterVersionId[param.version.id] ?? [],
      version: {
        ...param.version,
        confirmationMethod: parameterConfirmationMethods.get(param.version.procedureEquipmentId)!,
      },
    })),
  };
}
